import { z } from "zod";

// TES-1159 — the frequencies that mean exactly once a week, as doctors write
// them in the frequency field. Matched whole (case and surrounding space
// ignored), never searched inside a longer text: "en gång i veckan i 4
// veckor" or "varannan vecka" are not this, and anything not on the list
// gets no computed next dose. Ö-64 still holds — the text is shown as
// written; this only decides whether a date may be computed from it.
//
// TES-1375 (Ö-419) — and since Ella 2.1 this list is also the ONLY dose
// interval an ordination may carry. Emelie, TES-1362:
// "Den första läkemedelskatalogen omfattar Mounjaro, Wegovy och Ozempic, som alla doseras en gång i veckan."
// "Admin får inte tillåta en aktiv daglig eller oregelbunden ordination som appen inte kan presentera korrekt."
// The app presents an ordination through this list (Home's dose day, the
// next dose, the dose log), so an interval outside it is one the app cannot
// present. Supporting another interval needs its own versioned interval
// model first; adding a non-weekly spelling to this set is not that.
const ONCE_A_WEEK = new Set([
  "en gång i veckan",
  "en gång per vecka",
  // TES-1375 — Kirill's note shorthand (parsePrescriptionNote prefills it).
  "en gång/vecka",
  "1 gång i veckan",
  "1 gång per vecka",
  "1 gång/vecka",
  "1g/v",
  "varje vecka",
  "veckovis",
  "once a week",
  "once weekly",
  "weekly",
]);

export function isOnceAWeek(frequency: string | null | undefined): boolean {
  return ONCE_A_WEEK.has(
    (frequency ?? "").trim().toLowerCase().replace(/\s+/g, " "),
  );
}

/** The interval as an ordination's text, and the one admin offers. */
export const WEEKLY_FREQUENCY = "en gång i veckan";

// Staff copy (admin and the API's field errors). Words: Ö-603, Emelie
// 2026-10-21, verbatim. Never rewrite the doctor's intended ordination to fit
// the app; the prefilled message asks her to check it instead.
export const DOSE_INTERVAL_COPY = {
  required: "Välj doseringsintervall.",
  // The error when the interval is not weekly.
  weeklyOnly: "Det valda doseringsintervallet stöds inte i Ella 2.1.",
  // The standing line under the interval field and in the catalogue.
  hint: "Ella 2.1 stöder dosering en gång i veckan.",
  // A prefilled interval from an older order that is not weekly.
  notSupported: (written: string) =>
    `Intervallet ’${written}’ stöds inte i Ella 2.1. Kontrollera ordinationen och välj ett intervall som stöds.`,
} as const;

/**
 * The frequency of an order that becomes (or changes) the patient's
 * ordination: present, and once a week. The text is kept as written (one of
 * the weekly spellings above) because the patient reads it verbatim.
 */
export const weeklyFrequencySchema = z
  .string()
  .trim()
  .min(1, DOSE_INTERVAL_COPY.required)
  .max(64)
  // A blank interval is named by `min` alone, not as unsupported too.
  .refine((v) => v === "" || isOnceAWeek(v), DOSE_INTERVAL_COPY.weeklyOnly);
